import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse, HttpEventType, HttpInterceptorFn } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, tap, throwError } from 'rxjs';

import { AuthService } from '../auth.service';
import { ToastService } from '../components/toast-page/toast.service';
import { sanitizeInternalReturnUrl } from '../login-page/safe-return-url';
import { UserService } from '../user.service';
import { environment } from '../../environments/environment';
import {
  beginSessionExpiryRedirect,
  isLoginPath,
  isSessionAuthHttpError,
  noteAuthenticatedApiSuccess,
  rememberHandledSessionAuthFailure,
  runSessionExpiryDraftStash,
  SESSION_EXPIRED_TOAST_MESSAGE,
} from './session-auth-failure';

/**
 * Sessão morta na API (cookie ausente, token inválido/expirado ou revogado)
 * limpa o usuário local e abre o login com a URL atual.
 * 403 de papel insuficiente não entra aqui.
 */
export const sessionExpiredInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.startsWith(environment.apiUrl)) {
    return next(req);
  }

  const platformId = inject(PLATFORM_ID);
  const router = inject(Router);
  const toast = inject(ToastService);
  const auth = inject(AuthService);
  const user = inject(UserService);

  return next(req).pipe(
    tap((event) => {
      if (event.type === HttpEventType.Response) {
        noteAuthenticatedApiSuccess(req.url);
      }
    }),
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse && isSessionAuthHttpError(error)) {
        rememberHandledSessionAuthFailure(error);
        redirectAfterSessionExpiry(platformId, router, toast, auth, user);
      }
      return throwError(() => error);
    })
  );
};

function redirectAfterSessionExpiry(
  platformId: object,
  router: Router,
  toast: ToastService,
  auth: AuthService,
  user: UserService
): void {
  if (!isPlatformBrowser(platformId)) {
    return;
  }

  const pathname = window.location.pathname;
  if (isLoginPath(pathname)) {
    return;
  }
  if (!beginSessionExpiryRedirect()) {
    return;
  }

  runSessionExpiryDraftStash();
  auth.removeToken();
  user.clearUser();
  toast.show('Sessão', SESSION_EXPIRED_TOAST_MESSAGE, 'warning');

  const returnUrl = sanitizeInternalReturnUrl(
    `${pathname}${window.location.search}${window.location.hash}`
  );
  void router.navigate(['/login'], { queryParams: { returnUrl } });
}
